import React, { useState, useEffect, useCallback } from 'react';
import { Card, Form, Button, Modal, Table } from 'react-bootstrap';
import StatusBulb from './StatusBulb';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import DateInputDMY from './DateInputDMY';
import { formatDateOnly } from '../utils/formatDate';

const MS_PER_DAY = 1000 * 60 * 60 * 24;

const permitStatus = (expirationDate) => {
  const daysLeft = (new Date(expirationDate) - new Date()) / MS_PER_DAY;
  if (daysLeft < 0) return 'red';
  if (daysLeft <= 30) return 'amber';
  return 'green';
};

const emptyForm = { name: '', expirationDate: '', notes: '' };

const CompliancePanel = () => {
  const [permits, setPermits] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [permitToDelete, setPermitToDelete] = useState(null);

  const fetchPermits = useCallback(async () => {
    try {
      const res = await fetch('/api/compliance');
      const data = await res.json();
      setPermits(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('❌ Error fetching compliance permits:', err);
    }
  }, []);

  useEffect(() => { fetchPermits(); }, [fetchPermits]);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (permit) => {
    setEditingId(permit._id);
    setForm({
      name: permit.name,
      expirationDate: permit.expirationDate ? permit.expirationDate.slice(0, 10) : '',
      notes: permit.notes || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.expirationDate) return;
    try {
      const url = editingId ? `/api/compliance/${editingId}` : '/api/compliance';
      const method = editingId ? 'PUT' : 'POST';
      await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      setShowModal(false);
      fetchPermits();
    } catch (err) {
      console.error('❌ Error saving compliance permit:', err);
    }
  };

  const handleDelete = async () => {
    if (!permitToDelete) return;
    try {
      await fetch(`/api/compliance/${permitToDelete._id}`, { method: 'DELETE' });
      setPermitToDelete(null);
      fetchPermits();
    } catch (err) {
      console.error('❌ Error deleting compliance permit:', err);
    }
  };

  return (
    <div className="row mb-4">
      <div className="col-12">
        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <Card.Title className="mb-0">Cumplimiento y Permisos</Card.Title>
              <Button size="sm" onClick={openAdd}>Agregar</Button>
            </div>
            <div className="table-responsive">
              <Table size="sm" className="align-middle">
                <thead>
                  <tr>
                    <th></th>
                    <th>Permiso</th>
                    <th>Vencimiento</th>
                    <th>Notas</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {permits.length > 0 ? permits.map((permit) => (
                    <tr key={permit._id}>
                      <td><StatusBulb status={permitStatus(permit.expirationDate)} /></td>
                      <td>{permit.name}</td>
                      <td>{formatDateOnly(permit.expirationDate)}</td>
                      <td style={{ fontSize: '0.85rem', color: 'var(--mm-muted)' }}>{permit.notes}</td>
                      <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                        <Button size="sm" variant="outline-secondary" className="mr-1" onClick={() => openEdit(permit)}>Editar</Button>
                        <Button size="sm" variant="outline-danger" onClick={() => setPermitToDelete(permit)}>Eliminar</Button>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="5" className="text-muted text-center">Sin permisos registrados.</td></tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingId ? 'Editar' : 'Agregar'} Permiso</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2">
            <Form.Label>Nombre</Form.Label>
            <Form.Control
              type="text"
              placeholder='Ej. "Permiso de Bomberos"'
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Fecha de Vencimiento</Form.Label>
            <DateInputDMY
              value={form.expirationDate}
              onChange={(iso) => setForm({ ...form, expirationDate: iso })}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Notas</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
          <Button variant="primary" onClick={handleSave}>Guardar</Button>
        </Modal.Footer>
      </Modal>

      <ConfirmDeleteModal
        show={!!permitToDelete}
        onHide={() => setPermitToDelete(null)}
        onConfirm={handleDelete}
        message={<>¿Estás seguro que deseas eliminar el permiso <strong>{permitToDelete?.name}</strong>?</>}
      />
    </div>
  );
};

export default CompliancePanel;
